import { useEffect, useMemo, useRef, useState } from 'react';
import { keyBy } from 'lodash';
import PropTypes from 'prop-types';
import { FormField, RadioGroup } from 'taboola-ultimate-ui';
import { ChatAgentButtonBase } from 'components/ChatAgent/components/ChatAgentButtonBase/ChatAgentButtonBase';
import { FORM_MODES } from 'config/formModes';
import { useAvailableItems, useEntityType } from 'hooks';
import { ENTITY, PATH_TO_ENTITY_MAP } from 'modules/campaigns/config';
import { withIndication } from 'modules/errors';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useBudgetRecommendation } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useBudgetRecommendation';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage, FormattedNumber } from 'modules/taboola-common-frontend-modules/i18n';
import { BUDGET_TYPES } from '../../config';
import { BudgetSimulatorSection } from '../Bid/BudgetSimulatorSection/BudgetSimulatorSection';
import BudgetRadioChoice from './BudgetRadioChoice/BudgetRadioChoice';
import { BudgetRecommendationActionBar } from './BudgetRecommendationActionBar/BudgetRecommendationActionBar';
import { NoneSVGIcon } from './Icons/NoneSVGIcon';
import PaceAhead from './PaceAhead/PaceAhead';
import SpendingLimit from './SpendingLimit/SpendingLimit';
import { BUDGET_OPTIONS } from './budgetOptions';
import { getEmptyBudgetObject } from './const/emptyBudgetObject';
import { useBudgetAdvisorRecommendationTrigger } from './hooks/useBudgetAdvisorRecommendationTrigger';
import { useBudgetFormFieldValue } from './hooks/useBudgetFormFieldValue';
import { useFormBudgetValidatedValue } from './hooks/useFormBudgetValidatedValue';
import { useNoneSpendingLimitEnabled } from './hooks/useNoneSpendingLimitEnabled';
import styles from './budget.module.scss';

export const RadioGroupWithErrorMessage = withIndication(RadioGroup, styles['group-container']);
const isDailyBudget = type => type === BUDGET_TYPES.DAILY;

export const Budget = ({
    spent,
    isDisabled,
    isHidden,
    entityTypeMessagePrefix,
    disableBudgetTypeChange,
    budgetOptionsConditionData,
    showPaceAhead = false,
    helpTextId: helpTextIdProp,
    formFieldParams,
}) => {
    const {
        mode,
        formAccount: { currency },
    } = useFormDataContext();
    const { value: budget, throttledValue: throttledBudget, onChange: onBudgetChange } = useBudgetFormFieldValue();
    const {
        apply: acceptRecommendation,
        remove: removeRecommendation,
        recommendation,
    } = useBudgetRecommendation(onBudgetChange);
    const selectedBudgetType = budget?.type;
    const isSelectedBudgetTypeDaily = isDailyBudget(selectedBudgetType);
    const budgetCache = useRef({});
    const isNoneSpendingLimitEnabled = useNoneSpendingLimitEnabled();
    const entityType = useEntityType(PATH_TO_ENTITY_MAP);
    const isUnlimitedAsNoneEnabled = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_GROUP_BUDGET_UNLIMITED_AS_NONE_ENABLED]: 'true',
    });
    const options = useAvailableItems(BUDGET_OPTIONS, {
        ...budgetOptionsConditionData,
        entityType,
    });

    const budgetAdvisorPermitted = usePermissions('CHAT_AGENT_BUDGET_ADVISOR');
    useBudgetAdvisorRecommendationTrigger();

    const showBudgetAdvisor =
        budgetAdvisorPermitted &&
        isSelectedBudgetTypeDaily &&
        entityType === ENTITY.CAMPAIGN &&
        mode !== FORM_MODES.EDIT &&
        !recommendation;

    const validationData = useMemo(
        () => ({ skipValidation: keyBy(options, 'id')[selectedBudgetType]?.skipBudgetData || isDisabled, budget }),
        [options, selectedBudgetType, isDisabled, budget]
    );

    const { failedValidationData: failedBudgetValueValidationData, scrollRef: budgetValueScrollRef } =
        useFormBudgetValidatedValue(validationData);

    const onRadioChange = type =>
        onBudgetChange(prev => getEmptyBudgetObject({ prev, type, isNoneSpendingLimitEnabled, budgetCache }));

    const onValueChange = value => onBudgetChange(prev => ({ ...prev, value }));
    const [hoverKey, setHoverKey] = useState();
    const getDescriptionText = () => {
        const keyForDescription = hoverKey || selectedBudgetType;

        if (keyForDescription === BUDGET_TYPES.UNLIMITED) {
            if (isUnlimitedAsNoneEnabled) {
                return <FormattedMessage id={`${entityTypeMessagePrefix}.editor.budget.NONE.description`} />;
            }
        }
        const baseKey = `${entityTypeMessagePrefix}.editor.budget.${BUDGET_TYPES[keyForDescription]}.description`;
        return <FormattedMessage id={baseKey} />;
    };

    const handleMouseLeave = () => setHoverKey(null);
    const { value: selectedBudgetValue } = budget || {};
    const helpTextId = helpTextIdProp || `${entityTypeMessagePrefix}.editor.budget.help`;
    const showSpent = mode === FORM_MODES.EDIT && (spent || spent === 0);
    const showSpentUnderBudget =
        showSpent && (selectedBudgetType === BUDGET_TYPES.MONTHLY || selectedBudgetType === BUDGET_TYPES.LIFETIME);

    useEffect(() => {
        if (selectedBudgetType) {
            budgetCache.current[selectedBudgetType] = budget;
        }
    }, [selectedBudgetType, budget]);

    if (isHidden) {
        return null;
    }
    const getTitleAndLabelAndIcon = (type, id, icon) => {
        if (isUnlimitedAsNoneEnabled && type === BUDGET_TYPES.UNLIMITED) {
            return { titleId: 'campaign.editor.NONE', labelId: 'campaign.editor.budget.NONE', icon: <NoneSVGIcon /> };
        }
        return { titleId: `campaign.editor.${id}`, labelId: `campaign.editor.budget.${id}`, icon };
    };
    return (
        <FormField
            inputId="budget"
            label={<FormattedMessage id="campaign.editor.budget" defaultMessage="Budget" />}
            //ToDo: change back to normal ID after the chat agent is updated
            labelSuffix={
                showBudgetAdvisor && (
                    <ChatAgentButtonBase
                        id="CAMPAIGN_BUDGET_NEGOTIATION"
                        agentName="budget_negotiation"
                        messageId="abby.budget.negotiation"
                        className={styles['chat-agent-button']}
                    />
                )
            }
            description={
                <FormattedMessage
                    id={`${entityTypeMessagePrefix}.editor.budget.description`}
                    defaultMessage="You can specify a budget that is daily, monthly, or for the duration of the campaign 'lifetime'."
                />
            }
            dataTestId="budget-test-id"
            helpText={
                <FormattedMessage
                    id={`${helpTextId}`}
                    defaultMessage="Decide how much you would like to spend on your campaign - and how you would like to pace your budget."
                />
            }
            ref={budgetValueScrollRef}
            {...formFieldParams}
        >
            <BudgetSimulatorSection />
            {recommendation && (
                <BudgetRecommendationActionBar
                    budget={throttledBudget}
                    recommendation={recommendation}
                    onAccept={acceptRecommendation}
                    onClose={removeRecommendation}
                />
            )}
            <RadioGroupWithErrorMessage
                className={styles['group']}
                selectedValue={selectedBudgetType}
                onChange={onRadioChange}
                aria-label="Budget Radio"
                data-metrics-component="Budget"
                disabled={isDisabled}
                {...failedBudgetValueValidationData}
            >
                {options.map(({ id, type, icon, skipBudgetData, disabled }) => {
                    const option = getTitleAndLabelAndIcon(type, id, icon);
                    return (
                        <BudgetRadioChoice
                            id={id}
                            key={type}
                            containerClassName={styles['choice-container']}
                            className={styles['choice']}
                            title={<FormattedMessage id={option.titleId} />}
                            label={<FormattedMessage id={option.labelId} defaultMessage="Daily Budget" />}
                            skipBudgetData={skipBudgetData}
                            iconComponent={option.icon}
                            type={type}
                            disabled={disabled || (disableBudgetTypeChange && selectedBudgetType !== type)}
                            currency={currency}
                            value={selectedBudgetValue}
                            onBudgetChange={onValueChange}
                            onMouseEnter={setHoverKey}
                            onMouseLeave={handleMouseLeave}
                            failedValidationData={failedBudgetValueValidationData}
                        />
                    );
                })}
            </RadioGroupWithErrorMessage>
            {showSpentUnderBudget && (
                <div className={styles['spent']}>
                    <FormattedMessage
                        id="campaign.editor.budget.spent"
                        values={{
                            value: <FormattedNumber value={spent} variant="currency" currency={currency} />,
                        }}
                    />
                </div>
            )}
            <div className={styles['description']}>{getDescriptionText()}</div>
            <div className={styles['section']}>
                {isSelectedBudgetTypeDaily ? (
                    <SpendingLimit
                        id={`${selectedBudgetType}-SpendingLimit`}
                        inputId="spending-limit"
                        currency={currency}
                        disabled={isDisabled}
                        budget={budget}
                        spent={showSpent ? spent : null}
                        entityTypeMessagePrefix={entityTypeMessagePrefix}
                        disableTypeChange={disableBudgetTypeChange}
                    />
                ) : (
                    showPaceAhead && <PaceAhead />
                )}
            </div>
        </FormField>
    );
};

Budget.propTypes = {
    spent: PropTypes.number,
    isDisabled: PropTypes.bool,
    entityTypeMessagePrefix: PropTypes.string,
    disableBudgetTypeChange: PropTypes.bool,
    helpTextId: PropTypes.string,
    formFieldParams: PropTypes.object,
};
Budget.defaultProps = {
    formFieldParams: {},
};
